<template>
  <main id="main-content">
    <ApiLoading v-if="isApiLoading || isApiFail" :apiError="isApiFail" />
    <div class="feature-landing-page" v-else>
      <div class="content-page-banner">
        <div class="banner-content">
          <h1>{{ $route.meta.title }}</h1>
          <div class="underline"></div>
          <div v-if="doesPolicyHaveAnActiveRebate" v-html="refData.rebateOverviewDescriptionTextActive.informationText" />
          <div v-else v-html="refData.rebateOverviewDescriptionTextInactive.informationText" />
        </div>
      </div>
      <section class="content fr-view">
        <Alert
          v-if="isRebateUnprocessed"
          alertType="info"
          message="Your latest rebate change is currently being processed. Please wait until this update is finished processing before making another change. This can take up to 7 business days."
        />
        <Alert v-if="isRebateRejected" alertType="info" message="Your latest rebate change has been rejected. Please contact Defence Health for further assistance." />
        <Alert
          v-if="isPolicySuspended"
          alertType="info"
          message="You are unable to make changes to your rebate while in suspension. Once your policy is resumed, you can update your rebate."
        />
        <template>
          <div id="page-content">
            <div id="page-text">
              <div class="section-description" v-if="false">
                <h2 v-html="refData.managingYourRebateHeadingTextActive.informationText" />
                <p v-html="refData.managingYourRebateDescriptionTextActive.informationText" />
                <br />
                <h3 v-html="refData.managingYourRebateSubheading1TextActive.informationText" />
                <p v-html="refData.managingYourRebateSubheading1ParagraphTextActive.informationText" />
                <br />
                <h3 v-html="refData.managingYourRebateSubheading2TextActive.informationText" />
                <p v-html="refData.managingYourRebateSubheading2ParagraphTextActive.informationText" />
                <br />
                <h3 v-html="refData.managingYourRebateSubheading3TextActive.informationText" />
                <p v-html="refData.managingYourRebateSubheading3ParagraphTextActive.informationText" />
              </div>
            </div>
            <div id="page-buttons">
              <template v-if="!isViewRebateHistoryAllowed && isAddARebateAllowed && false">
                <div class="call-out call-to-action box">
                  <div class="content-container">
                    <figure>
                      <img src="/assets/public/images/icon_add.svg" alt="callout-icon" />
                    </figure>
                    <h3>Add a rebate</h3>
                    <p v-html="refData.addARebateCallToActionSubtitleTextInactive.informationText" />
                    <button @click="navigateToPage(REBATES_MEMBER_PORTAL.ADD_REBATE)" class="button crimson">Add rebate</button>
                  </div>
                </div>
              </template>
              <template v-else>
                <TaskButton
                  v-if="isViewRebateHistoryAllowed"
                  imageSrc="/assets/public/images/rebate-history-icon.svg"
                  imageAlt="view-rebate-history-button-icon"
                  :hasArrow="true"
                  tabindex="1"
                  :onClick="() => navigateToPage(REBATES_MEMBER_PORTAL.REBATE_TIER_HISTORY)"
                  :enterDown="() => navigateToPage(REBATES_MEMBER_PORTAL.REBATE_TIER_HISTORY)"
                >
                  View rebate tier history
                </TaskButton>
                <TaskButton
                  v-if="isAddARebateAllowed && false"
                  :hasArrow="true"
                  tabindex="2"
                  :onClick="() => navigateToPage(REBATES_MEMBER_PORTAL.ADD_REBATE)"
                  :enterDown="() => navigateToPage(REBATES_MEMBER_PORTAL.ADD_REBATE)"
                  imageSrc="/assets/public/images/add-a-rebate-icon.svg"
                  imageAlt="add-rebate-button-icon"
                >
                  Add a rebate
                </TaskButton>
                <TaskButton
                  v-if="isUpdateRebateAllowed && false"
                  imageSrc="/assets/public/images/update-rebate-icon.svg"
                  imageAlt="update-rebate-tier-button-icon"
                  :hasArrow="true"
                  tabindex="3"
                  :onClick="() => navigateToPage(REBATES_MEMBER_PORTAL.UPDATE_REBATE)"
                  :enterDown="() => navigateToPage(REBATES_MEMBER_PORTAL.UPDATE_REBATE)"
                >
                  Update your rebate tier
                </TaskButton>
                <TaskButton
                  v-if="isWithdrawRebateAllowed && false"
                  imageSrc="/assets/public/images/withdraw-rebate-icon.svg"
                  imageAlt="withdraw-rebate-button-icon"
                  :hasArrow="true"
                  tabindex="4"
                  :onClick="() => navigateToPage(REBATES_MEMBER_PORTAL.WITHDRAW_REBATE)"
                  :enterDown="() => navigateToPage(REBATES_MEMBER_PORTAL.WITHDRAW_REBATE)"
                >
                  Withdraw your rebate
                </TaskButton>
              </template>
            </div>
          </div>
        </template>
      </section>
    </div>
  </main>
</template>

<script>
import TaskButton from '@general/js/components/TaskButton.vue';
import RebateServices from '@/services/MyCover/Rebates';
import { ApiLoading, Alert } from '@/components';
// import { sortByObjectPropertyWithPriority } from '@/utils';
import { ERROR_404_PAGE_NOT_FOUND, REBATES_MEMBER_PORTAL } from '@/router/routeNames';
import { AccessLevel } from '@/enums';
import RebateMixins from './RebateMixins';

export default {
  name: 'RebateLanding',
  components: { TaskButton, ApiLoading, Alert },
  mixins: [RebateMixins],

  data() {
    return {
      apiStatus: {
        refData: {
          loading: false,
          fail: false,
        },
        rebateHistory: {
          loading: false,
          fail: false,
        },
      },
      refData: {},
      REBATES_MEMBER_PORTAL,
    };
  },

  beforeMount() {
    if (this.accessLevel?.toLowerCase() === AccessLevel.SELF_ACCESS) {
      this.$router.push({ name: ERROR_404_PAGE_NOT_FOUND });
    }

    this.getRebateHistory();
    this.fetchRebateLandingRefData();
  },

  computed: {
    isApiLoading() {
      return this.apiStatus.refData.loading || this.apiStatus.rebateHistory.loading;
    },

    isApiFail() {
      return this.apiStatus.refData.fail || this.apiStatus.rebateHistory.fail;
    },
  },

  methods: {
    // Rebate history is fetched on landing page as it is used to determine which buttons to show/hide
    async getRebateHistory() {
      this.apiStatus.rebateHistory.loading = true;
      this.apiStatus.rebateHistory.fail = false;
      try {
        const res = await RebateServices.retrieveRebateHistory(this.memberNumber);
        this.updateRebateHistory(res);
      } catch (error) {
        this.apiStatus.rebateHistory.fail = true;
        logger.log('Error fetching rebate history', error);
      } finally {
        this.apiStatus.rebateHistory.loading = false;
      }
    },

    async fetchRebateLandingRefData() {
      this.apiStatus.refData.loading = true;
      this.apiStatus.refData.fail = false;
      try {
        this.refData = await RebateServices.getRebateLandingRefData();
      } catch (error) {
        this.apiStatus.refData.fail = true;
        logger.log('Error fetching rebate landing ref data', error);
      } finally {
        this.apiStatus.refData.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
#page-content {
  @include flex-row;
  justify-content: space-between;
  column-gap: 10%;
  padding: 56px 0;

  @include mobile {
    flex-direction: column;
  }
}

.section-description {
  max-width: rem(700);
  margin-bottom: rem(50);
}

:deep(h3) {
  font-size: $DESKTOP_H4_SIZE;
}
</style>
